import React, { useEffect, useRef, useState } from 'react'
import Layout from '../components/Layout.js'

const Developers = () => {
  const [activeSection, setActiveSection] = useState(null);
  const overviewRef = useRef(null);
  const architectureRef = useRef(null);
  const lexiconsRef = useRef(null);
  const endpointsRef = useRef(null);
  const authRef = useRef(null);
  const resourcesRef = useRef(null);

  const sections = [
    { name: 'overview', ref: overviewRef },
    { name: 'architecture', ref: architectureRef },
    { name: 'lexicons', ref: lexiconsRef },
    { name: 'endpoints', ref: endpointsRef },
    { name: 'auth', ref: authRef },
    { name: 'resources', ref: resourcesRef },
  ];

  const handleScroll = () => {
    const currentSection = sections.find(section => {
      const element = section.ref.current;
      if (element) {
        const { offsetTop } = element;
        const screenPosition = window.scrollY + window.innerHeight / 2;
        return screenPosition > offsetTop && element.getBoundingClientRect().bottom >= 50;
      }
      return false;
    });

    setActiveSection(currentSection ? currentSection.name : null);
  };

  const handleClick = (jumpToSectionRef) => {
    jumpToSectionRef.current.scrollIntoView({ behavior: 'smooth' })
    const currentSection = sections.find(section => {
      return section.ref === jumpToSectionRef;
    })
    setActiveSection(currentSection.name)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  const sidebarItem = (name, label, ref) => (
    <li
      className={`${
        activeSection === name
          ? "text-gray-900 dark:text-white"
          : "text-gray-600 dark:text-gray-500"
      } cursor-pointer my-6 text-base font-light hover:text-gray-900 dark:hover:text-white`}
      onClick={() => handleClick(ref)}
    >
      {label}
    </li>
  );

  const sectionHeading = (text) => (
    <h2 className="my-4 text-lg tracking-wider text-gray-600 dark:text-gray-500 md:text-xl lg:text-2xl">
      {text}
    </h2>
  );

  const sectionParagraph = (text) => (
    <p className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
      {text}
    </p>
  );

  const subHeading = (text) => (
    <h3 className="mt-8 mb-3 text-base tracking-wider text-gray-900 dark:text-white md:text-lg">
      {text}
    </h3>
  );

  const codeBlock = (code) => (
    <pre className="my-4 p-4 rounded-lg bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 overflow-x-auto">
      <code className="text-[11px] lg:text-[13px] text-gray-800 dark:text-gray-200 font-mono leading-relaxed whitespace-pre">
        {code}
      </code>
    </pre>
  );

  const inlineCode = (text) => (
    <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-purple-600 dark:text-purple-400 text-[11px] lg:text-[13px] font-mono">
      {text}
    </code>
  );

  const externalLink = (href, text) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-purple-600 dark:text-purple-400 hover:text-pink-600 dark:hover:text-pink-400 transition-colors duration-300 underline decoration-purple-600/30 dark:decoration-purple-400/30 hover:decoration-pink-600 dark:hover:decoration-pink-400"
    >
      {text}
    </a>
  );

  const endpointRow = (method, path, description, auth) => (
    <tr className="border-b border-gray-200 dark:border-gray-700">
      <td className="py-2 pr-3 align-top">
        <span className="inline-block px-2 py-0.5 rounded text-[10px] lg:text-[11px] font-mono font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          {method}
        </span>
      </td>
      <td className="py-2 pr-3 align-top font-mono text-[11px] lg:text-[12px] text-gray-900 dark:text-white whitespace-nowrap">
        {path}
      </td>
      <td className="py-2 pr-3 align-top text-[11px] lg:text-[12px] text-gray-600 dark:text-gray-400">
        {description}
      </td>
      <td className="py-2 align-top text-[11px] lg:text-[12px] text-gray-500 dark:text-gray-500 whitespace-nowrap">
        {auth}
      </td>
    </tr>
  );

  const fieldRow = (name, type, required, description) => (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className="py-1.5 pr-3 align-top font-mono text-[11px] lg:text-[12px] text-purple-600 dark:text-purple-400">
        {name}
      </td>
      <td className="py-1.5 pr-3 align-top font-mono text-[11px] lg:text-[12px] text-gray-500 dark:text-gray-500">
        {type}
      </td>
      <td className="py-1.5 pr-3 align-top text-[11px] lg:text-[12px] text-gray-600 dark:text-gray-400">
        {required ? 'Required' : 'Optional'}
      </td>
      <td className="py-1.5 align-top text-[11px] lg:text-[12px] text-gray-600 dark:text-gray-400">
        {description}
      </td>
    </tr>
  );

  return (
    <Layout title="Developer Docs" marginBottomClass={"mb-0"}>
      <section className="font-mono xl:px-12">
        <h1 className="text-gray-900 dark:text-white mx-auto mt-[50px] mb-[50px] text-[50px] tracking-wide max-md:text-[30px] max-md:mt-[30px] max-md:mb-[50px] md:max-lg:text-[30px]">
          <span className="text-gradient from-purple-600 to-pink-600 pl-1.5 pr-1.5">
            AppView Developer Docs
          </span>
        </h1>
        <div className="flex flex-col border-t border-gray-300 dark:border-gray-700 md:flex-row">
          <div className="hidden w-0 lg:h-full lg:sticky lg:top-[90px] lg:w-1/4 lg:justify-left lg:items-center lg:flex lg:flex-1 lg:text-2xl lg:mt-4">
            <ul>
              {sidebarItem('overview', 'Overview', overviewRef)}
              {sidebarItem('architecture', 'Architecture', architectureRef)}
              {sidebarItem('lexicons', 'Lexicons', lexiconsRef)}
              {sidebarItem('endpoints', 'API Endpoints', endpointsRef)}
              {sidebarItem('auth', 'Authentication', authRef)}
              {sidebarItem('resources', 'Resources', resourcesRef)}
            </ul>
          </div>
          <div className="w-full pl-1.5 lg:border-l lg:border-gray-300 lg:dark:border-gray-700 lg:w-3/4 lg:pl-10">

            {/* Overview */}
            <div ref={overviewRef} className="pt-16">
              {sectionHeading('Overview')}
              {sectionParagraph(
                'The Effem AppView is a custom AT Protocol service that powers the social and podcast features of the Effem ecosystem. ' +
                'It consumes records from the AT Protocol firehose, indexes Effem-specific data, and exposes a read-only XRPC API for clients.'
              )}
              <div className="mt-4" />
              {sectionParagraph(
                'In AT Protocol architecture, an AppView reads from user repositories (via the firehose) and serves aggregated, query-optimized views of that data. ' +
                'Writes happen directly to the user\'s Personal Data Server (PDS) using standard AT Proto repo operations. ' +
                'The AppView picks up those writes from the relay firehose and indexes them into its own database.'
              )}
              <div className="mt-4" />
              {sectionParagraph(
                'The Effem AppView also acts as a proxy to the Podcast Index API, enriching podcast and episode metadata with social counts ' +
                '(subscribers, comments, recommendations) from its own indexed data.'
              )}
              {subHeading('Key Concepts')}
              <ul className="list-none space-y-3 mt-4">
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">Lexicons</strong> — AT Protocol schema definitions that describe the record types stored in user repositories. Effem defines its own lexicons under the {inlineCode('xyz.effem.*')} namespace.
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">XRPC</strong> — The RPC framework used by AT Protocol services. All AppView query endpoints are served under {inlineCode('/xrpc/')}.
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">Firehose</strong> — A real-time stream of repository events from the AT Protocol relay. The AppView subscribes to this stream and indexes {inlineCode('xyz.effem.*')} records.
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">Podcast Index</strong> — An open podcast directory. Effem uses Podcast Index feed and episode IDs as canonical identifiers across all lexicons.
                </li>
              </ul>
            </div>

            {/* Architecture */}
            <div ref={architectureRef} className="pt-16">
              {sectionHeading('Architecture')}
              {sectionParagraph(
                'The AppView follows a three-stage pipeline: Firehose Consumer, Indexer, and API layer.'
              )}

              {subHeading('Firehose Consumer')}
              {sectionParagraph(
                'Connects to the AT Protocol relay via WebSocket and subscribes to com.atproto.sync.subscribeRepos. ' +
                'Commits are processed in parallel (configurable worker pool). Only records in the xyz.effem.* namespace are indexed; everything else is ignored. ' +
                'The consumer persists its cursor position periodically so it can resume after restarts without reprocessing the entire history.'
              )}

              {subHeading('Indexer')}
              {sectionParagraph(
                'Routes decoded records to type-specific handlers based on the collection name. All writes use an upsert pattern keyed on (DID, rkey) for idempotent processing. ' +
                'After every social record change (subscription, comment, recommendation, bookmark), materialized stats tables are refreshed to keep aggregated counts current.'
              )}

              {subHeading('API Layer')}
              {sectionParagraph(
                'An HTTP server (Echo framework) exposes read-only XRPC endpoints. Podcast and episode queries proxy to the Podcast Index API ' +
                'with a database-backed cache (configurable TTL per endpoint type), then inject social counts from the materialized stats tables. ' +
                'User-specific endpoints (subscriptions, bookmarks, episode states) enforce ownership via middleware.'
              )}

              {subHeading('Data Flow')}
              {codeBlock(
`User writes record to PDS (e.g. creates a subscription)
       |
       v
AT Protocol Relay broadcasts commit via firehose
       |
       v
Effem AppView firehose consumer receives commit
       |
       v
Indexer decodes CBOR record, upserts into PostgreSQL
       |
       v
Materialized stats refreshed (subscriber/comment counts)
       |
       v
Client queries AppView XRPC endpoint
       |
       v
AppView returns indexed data + Podcast Index metadata`
              )}
            </div>

            {/* Lexicons */}
            <div ref={lexiconsRef} className="pt-16">
              {sectionHeading('Lexicons')}
              {sectionParagraph(
                'Effem defines its lexicons under the xyz.effem namespace. These schemas describe the record types that users store in their AT Protocol repositories. ' +
                'Records are written to the user\'s PDS and indexed by the AppView via the firehose.'
              )}

              {/* Canonical Records */}
              {subHeading('Canonical Records')}
              <p className="text-[12px] lg:text-[14px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Podcasts and episodes are first-class records in their own right. Social records (subscriptions, comments, recommendations, bookmarks, etc.) point at them via {inlineCode('com.atproto.repo.strongRef')} (AT URI + CID), rather than embedding identifier blobs inline.
              </p>

              <p className="mt-4 mb-2 text-[12px] lg:text-[13px] font-medium text-gray-900 dark:text-white tracking-wider">xyz.effem.feed.podcast</p>
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Canonical record for a podcast feed. Subject of subscriptions and feed-level engagement via {inlineCode('com.atproto.repo.strongRef')}.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('podcastGuid', 'string', true, 'Podcasting 2.0 podcast:guid value')}
                    {fieldRow('title', 'string', true, 'Up to 1024 chars')}
                    {fieldRow('feedUrl', 'string (uri)', false, 'RSS feed URL')}
                    {fieldRow('author', 'string', false, 'Up to 1024 chars')}
                    {fieldRow('artworkUrl', 'string (uri)', false, 'Artwork URL')}
                    {fieldRow('language', 'string', false, 'Up to 32 chars')}
                    {fieldRow('podcastIndex', 'object', false, 'Cross-reference for Podcast Index ({ feedId })')}
                  </tbody>
                </table>
              </div>

              <p className="mt-6 mb-2 text-[12px] lg:text-[13px] font-medium text-gray-900 dark:text-white tracking-wider">xyz.effem.feed.episode</p>
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Canonical record for a podcast episode. Subject of comments, recommendations, bookmarks, and episode-state records via {inlineCode('com.atproto.repo.strongRef')}.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('podcastGuid', 'string', true, 'Podcasting 2.0 podcast:guid')}
                    {fieldRow('episodeGuid', 'string', true, 'Episode GUID from the RSS feed')}
                    {fieldRow('title', 'string', true, 'Up to 1024 chars')}
                    {fieldRow('publishedAt', 'datetime', true, 'Episode publish time')}
                    {fieldRow('feedUrl', 'string (uri)', false, 'RSS feed URL')}
                    {fieldRow('enclosureUrl', 'string (uri)', false, 'Audio enclosure URL')}
                    {fieldRow('durationS', 'integer', false, 'Episode duration in seconds')}
                    {fieldRow('podcastIndex', 'object', false, 'Cross-reference for Podcast Index ({ feedId, episodeId })')}
                  </tbody>
                </table>
              </div>

              {/* Subscription */}
              {subHeading('xyz.effem.feed.subscription')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Declares a podcast subscription. Stored in the user's repo with a {inlineCode('tid')} key.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'strongRef', true, 'strongRef to an xyz.effem.feed.podcast record')}
                    {fieldRow('createdAt', 'datetime', true, 'When the subscription was created')}
                  </tbody>
                </table>
              </div>

              {/* Comment */}
              {subHeading('xyz.effem.feed.comment')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A comment on a podcast episode. Supports threading via reply references and rich text via Bluesky-compatible facets.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'strongRef', true, 'strongRef to an xyz.effem.feed.episode record')}
                    {fieldRow('text', 'string', true, '1-300 graphemes. The comment body.')}
                    {fieldRow('facets', 'array', false, 'Rich text facets (mentions, links, hashtags). Uses app.bsky.richtext.facet schema.')}
                    {fieldRow('reply', 'replyRef', false, 'Thread reference (root + parent AT URIs with CIDs)')}
                    {fieldRow('timestamp', 'integer', false, 'Episode playhead in seconds at the moment the comment was composed')}
                    {fieldRow('langs', 'string[]', false, 'Up to 3 BCP-47 language tags')}
                    {fieldRow('labels', 'selfLabels', false, 'Author-applied content warning labels (com.atproto.label.defs#selfLabels)')}
                    {fieldRow('createdAt', 'datetime', true, 'When the comment was created')}
                    {fieldRow('updatedAt', 'datetime', false, 'Stamped when the author edits the comment via putRecord')}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[11px] lg:text-[12px] text-gray-500 dark:text-gray-500 tracking-wider leading-relaxed font-light">
                The {inlineCode('replyRef')} object contains {inlineCode('root')} and {inlineCode('parent')} fields, both {inlineCode('com.atproto.repo.strongRef')} (AT URI + CID), enabling threaded comment trees.
              </p>

              {/* Recommendation */}
              {subHeading('xyz.effem.feed.recommendation')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A recommendation (like/upvote) for a podcast episode. Optionally includes a short explanation.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'strongRef', true, 'strongRef to an xyz.effem.feed.episode record')}
                    {fieldRow('text', 'string', false, '1-100 graphemes. Why this episode is great.')}
                    {fieldRow('createdAt', 'datetime', true, 'When the recommendation was created')}
                  </tbody>
                </table>
              </div>

              {/* List */}
              {subHeading('xyz.effem.feed.list')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A curated list of podcasts (e.g. "My startup pods", "True crime essentials").
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('name', 'string', true, '1-50 graphemes. List title.')}
                    {fieldRow('description', 'string', false, '1-300 graphemes. List description.')}
                    {fieldRow('podcasts', 'strongRef[]', true, '1-200 strongRefs to xyz.effem.feed.podcast records.')}
                    {fieldRow('createdAt', 'datetime', true, 'When the list was created')}
                  </tbody>
                </table>
              </div>

              {/* Bookmark */}
              {subHeading('xyz.effem.feed.bookmark')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A public bookmark on an episode. Optionally pinned to a specific timestamp.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'strongRef', true, 'strongRef to an xyz.effem.feed.episode record')}
                    {fieldRow('timestamp', 'integer', false, 'Episode playhead in seconds at which the bookmark was placed')}
                    {fieldRow('createdAt', 'datetime', true, 'When the bookmark was created')}
                  </tbody>
                </table>
              </div>

              {/* Episode State */}
              {subHeading('xyz.effem.feed.episodeState')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Per-user, per-episode playback and library state. The rkey is derived from the episode's catalog rkey, giving one record per episode per user.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'strongRef', true, 'strongRef to an xyz.effem.feed.episode record')}
                    {fieldRow('positionS', 'integer', false, 'Playback position in seconds')}
                    {fieldRow('durationS', 'integer', false, 'Total episode duration in seconds')}
                    {fieldRow('played', 'boolean', false, 'Marked as fully played')}
                    {fieldRow('saved', 'boolean', false, 'Saved to library/queue')}
                    {fieldRow('hidden', 'boolean', false, 'Archived/dismissed by user')}
                    {fieldRow('createdAt', 'datetime', true, 'When the state was created')}
                    {fieldRow('updatedAt', 'datetime', false, 'When the state was last modified')}
                  </tbody>
                </table>
              </div>

              {/* Profile */}
              {subHeading('xyz.effem.actor.profile')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                Effem-specific profile data that supplements the user's AT Protocol identity. Singleton record with key {inlineCode('self')}.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('displayName', 'string', false, 'Up to 64 graphemes')}
                    {fieldRow('description', 'string', false, 'Up to 256 graphemes')}
                    {fieldRow('avatar', 'blob', false, 'Profile avatar. PNG, JPEG, or WebP, up to 1 MB.')}
                    {fieldRow('favoriteGenres', 'string[]', false, 'Up to 10 genre strings (32 graphemes each)')}
                    {fieldRow('labelerSubscriptions', 'did[]', false, 'Up to 20 labeler DIDs whose labels appear in this user\'s read responses')}
                  </tbody>
                </table>
              </div>

              {/* Block */}
              {subHeading('xyz.effem.graph.block')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A block of another user. Blocks are mutual — both users' content is hidden from each other in all social endpoints.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'did', true, 'DID of the user being blocked')}
                    {fieldRow('createdAt', 'datetime', true, 'When the block was created')}
                  </tbody>
                </table>
              </div>

              {/* Report */}
              {subHeading('xyz.effem.moderation.report')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-2">
                A moderation report submitted by a user against a comment or user.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody>
                    {fieldRow('subject', 'string', true, 'AT URI of a comment or DID of a user')}
                    {fieldRow('reasonType', 'string', true, 'Known values: spam, abuse, harassment, other')}
                    {fieldRow('reason', 'string', false, 'Freeform detail (max 1000 chars)')}
                    {fieldRow('createdAt', 'datetime', true, 'When the report was submitted')}
                  </tbody>
                </table>
              </div>
            </div>

            {/* API Endpoints */}
            <div ref={endpointsRef} className="pt-16">
              {sectionHeading('API Endpoints')}
              {sectionParagraph(
                'All endpoints are served under /xrpc/ and accept GET requests. Responses follow standard XRPC conventions. ' +
                'Paginated endpoints accept limit and cursor query parameters and return a cursor field for the next page.'
              )}

              {subHeading('Feed Endpoints')}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider"></th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Endpoint</th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Description</th>
                      <th className="pb-2 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpointRow('GET', 'xyz.effem.feed.getSubscriptions', 'List a user\'s podcast subscriptions. Requires did query param.', 'Owner')}
                    {endpointRow('GET', 'xyz.effem.feed.getSubscribers', 'List subscribers to a podcast by feedId.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getComments', 'List comments on an episode by feedId and episodeId.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getCommentThread', 'Get a comment thread by AT URI (root + all replies).', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getRecommendations', 'List episode recommendations. Filter by feedId or episodeId.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getPopular', 'Top episodes by recommendation count. Filter by period (day/week/month).', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getList', 'Get a single podcast list by AT URI.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.feed.getLists', 'List a user\'s podcast lists. Requires did query param.', 'Owner')}
                    {endpointRow('GET', 'xyz.effem.feed.getBookmarks', 'List a user\'s bookmarks. Requires did query param.', 'Owner')}
                    {endpointRow('GET', 'xyz.effem.feed.getEpisodeStates', 'List a user\'s episode playback states. Requires did query param.', 'Owner')}
                    {endpointRow('GET', 'xyz.effem.feed.getBlocks', 'List a user\'s blocked DIDs. Requires did query param.', 'Owner')}
                    {endpointRow('GET', 'xyz.effem.feed.getInbox', 'Personalized episode feed from user\'s subscriptions. Requires did query param.', 'Owner')}
                  </tbody>
                </table>
              </div>

              {subHeading('Actor Endpoints')}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider"></th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Endpoint</th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Description</th>
                      <th className="pb-2 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpointRow('GET', 'xyz.effem.actor.getProfile', 'Get a user\'s Effem profile with aggregate counts (subscriptions, comments, recommendations, bookmarks, lists).', 'Read')}
                  </tbody>
                </table>
              </div>

              {subHeading('Podcast Endpoints')}
              <p className="text-[11px] lg:text-[13px] text-gray-600 dark:text-gray-400 tracking-wider leading-loose font-light mb-3">
                These proxy to the Podcast Index API and enrich responses with social counts from the AppView database.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider"></th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Endpoint</th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Description</th>
                      <th className="pb-2 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpointRow('GET', 'xyz.effem.podcast.getPodcast', 'Get podcast metadata by feedId or itunesId, with social counts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getEpisodes', 'List episodes for a podcast by feedId or feedUrl, with social counts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getEpisode', 'Get a single episode by episodeId, with social counts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getTrending', 'Trending podcasts from Podcast Index, with social counts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getCategories', 'List Podcast Index categories.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getRecentEpisodes', 'Recently published episodes across all podcasts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.podcast.getStats', 'Podcast Index API stats.', 'Read')}
                  </tbody>
                </table>
              </div>

              {subHeading('Search Endpoints')}
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-300 dark:border-gray-600">
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider"></th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Endpoint</th>
                      <th className="pb-2 pr-3 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Description</th>
                      <th className="pb-2 text-[11px] lg:text-[12px] font-medium text-gray-500 dark:text-gray-400 tracking-wider">Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpointRow('GET', 'xyz.effem.search.podcasts', 'Search podcasts by term, title, or person. Results enriched with social counts.', 'Read')}
                    {endpointRow('GET', 'xyz.effem.search.episodes', 'Search episodes by term.', 'Read')}
                  </tbody>
                </table>
              </div>

              {subHeading('Pagination')}
              {sectionParagraph(
                'Paginated endpoints use cursor-based pagination with TID (Timestamp ID) keys. Pass the cursor from the previous response to get the next page. ' +
                'The default and maximum limit varies per endpoint. When no more results are available, the cursor field is omitted from the response.'
              )}
              {codeBlock(
`GET /xrpc/xyz.effem.feed.getComments?feedId=123&episodeId=456&limit=25

{
  "comments": [...],
  "cursor": "3kf5g7h8j9k0"
}

GET /xrpc/xyz.effem.feed.getComments?feedId=123&episodeId=456&limit=25&cursor=3kf5g7h8j9k0`
              )}
            </div>

            {/* Authentication */}
            <div ref={authRef} className="pt-16">
              {sectionHeading('Authentication')}
              {sectionParagraph(
                'The AppView uses token-based authentication. Tokens are passed via the Authorization header (Bearer scheme) or the X-API-Key header. ' +
                'Each token maps to a DID (the user\'s identity) and a set of scopes.'
              )}

              {subHeading('Scopes')}
              <ul className="list-none space-y-3 mt-4">
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">read</strong> — Required for all XRPC endpoints. Grants access to public data.
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  <strong className="text-gray-900 dark:text-white">admin</strong> — Grants read access plus administrative capabilities.
                </li>
              </ul>

              {subHeading('Owner-Restricted Endpoints')}
              {sectionParagraph(
                'Endpoints marked "Owner" in the table above enforce that the did query parameter matches the authenticated user\'s DID. ' +
                'This prevents users from reading other users\' private data (subscriptions, bookmarks, episode states, etc.). ' +
                'Admin and service tokens bypass this restriction.'
              )}

              {subHeading('Example Request')}
              {codeBlock(
`curl -H "Authorization: Bearer <token>" \\
  "https://appview.effem.xyz/xrpc/xyz.effem.feed.getComments?feedId=123&episodeId=456&limit=10"`
              )}

              {subHeading('Block Filtering')}
              {sectionParagraph(
                'Social endpoints (comments, recommendations, bookmarks) automatically exclude content from users that the authenticated user has blocked, and vice versa. ' +
                'This is handled transparently by the AppView — clients do not need to implement their own filtering.'
              )}
            </div>

            {/* Resources */}
            <div ref={resourcesRef} className="pt-16">
              {sectionHeading('Resources')}
              {sectionParagraph(
                'Learn more about the protocols and services that Effem builds on.'
              )}

              {subHeading('AT Protocol')}
              <ul className="list-none space-y-3 mt-4">
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://atproto.com', 'atproto.com')} — AT Protocol homepage and documentation
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://atproto.com/specs/lexicon', 'Lexicon Specification')} — How AT Protocol schemas work
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://atproto.com/specs/xrpc', 'XRPC Specification')} — The RPC framework used by AT Protocol
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://atproto.com/specs/atp', 'AT Protocol Overview')} — Architecture, identity, and data model
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://atproto.com/guides/applications', 'Building on AT Protocol')} — Guide for application developers
                </li>
              </ul>

              {subHeading('Podcast Index')}
              <ul className="list-none space-y-3 mt-4">
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://podcastindex.org', 'podcastindex.org')} — Podcast Index homepage
                </li>
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://podcastindex-org.github.io/docs-api/', 'Podcast Index API Documentation')} — Full API reference
                </li>
              </ul>

              {subHeading('Source Code')}
              <ul className="list-none space-y-3 mt-4">
                <li className="text-[12px] lg:text-[14px] text-gray-900 dark:text-white tracking-wider leading-loose font-light">
                  {externalLink('https://tangled.org/sparrowtek.com', 'SparrowTek on Tangled')} — Source code repositories
                </li>
              </ul>
            </div>

            <div className="pt-16 pb-24" />
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Developers
